import React, { useEffect, useState, memo, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import { Box, Typography } from '@mui/material'

import CustomButton from 'components/CustomButton'
import { CaseHeroRoles, CaseHeroStates, CaseMasterScreens } from './constant'

// Geometry of the card deck in the right panel. The height matches what the
// single static illustration used to occupy, so the panel keeps its proportions.
// The width follows the front sheet's own aspect ratio, and SLOT_OFFSET is how
// far each card behind it peeks out to the right.
const DECK_SIZE = CaseHeroStates.length;
const CARD_HEIGHT = 160;
const CARD_WIDTH = 114;
const SLOT_OFFSET = 44;

// How much a card gives up for each place it sits further back in the queue.
const SLOT_SCALE_STEP = 0.055;
const SLOT_OPACITY_STEP = 0.05;

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  cardRoot: {
    borderRadius: theme.spacing(1),
    boxShadow: `0px 0px 4px 0px #00000026`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    padding: theme.spacing(6),
    width: '100%'
  },
  panels: {
    display: 'flex',
    gap: theme.spacing(6),
    width: '100%',
    // Side by side is tight once the sidebar is accounted for, so they stack
    // rather than squeezing the illustrations.
    [theme.breakpoints.down('1400')]: {
      flexDirection: 'column'
    }
  },
  // Padding, radius and gap all follow the interview wizard's Introduction, so
  // the two sections read as the same component at the same scale.
  panel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    // Top aligned, so this panel's heading sits level with the taller panel's
    // heading beside it rather than being centred lower down.
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    minHeight: '212px'
  },
  expectPanel: {
    backgroundColor: theme.palette.danger['clr-100'],
  },
  coveredPanel: {
    backgroundColor: theme.palette.success['clr-100'],
  },
  panelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4.5),
    minWidth: 0
  },
  // Custom theme variants fall through MUI's variantMapping to an inline
  // <span>, so these two need a column to sit on separate lines.
  roleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    // Descriptions differ in length and wrap to one or two lines; a floor keeps
    // the button below from shifting as the states rotate. Raised with the type:
    // a 28px title over a 16px description needs 60px before either wraps.
    minHeight: '84px'
  },
  panelImage: {
    flexShrink: 0,
    // The text is top aligned; the art stays centred in the panel.
    alignSelf: 'center',
    height: '160px',
    width: 'auto',
    objectFit: 'contain'
  },
  '@keyframes stateFade': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 }
  },
  // Keyed on its content, so advancing a state replays the transition:
  // 450ms ease in and out, per the animation spec.
  fadeState: {
    animation: '$stateFade 0.45s ease-in-out'
  },
  // The deck is one card per state, fanned out to the right. Cards are taken
  // out of flow and placed by transform alone, so the container has to carry
  // the full fanned-out footprint itself.
  deck: {
    position: 'relative',
    flexShrink: 0,
    alignSelf: 'center',
    width: `${CARD_WIDTH + ((DECK_SIZE - 1) * SLOT_OFFSET)}px`,
    height: `${CARD_HEIGHT}px`
  },
  deckCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: `${CARD_WIDTH}px`,
    height: `${CARD_HEIGHT}px`,
    // Reserved whether or not this card is the highlighted one, so taking the
    // front does not nudge the artwork by the width of a border.
    border: '2px solid transparent',
    boxSizing: 'border-box',
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxShadow: '0px 2px 8px 0px #00000014',
    // Smart Animate: the card holds its identity and slides to its new place in
    // the queue rather than crossfading with the card that replaces it.
    transition: 'transform 0.45s ease-in-out, opacity 0.45s ease-in-out, border-color 0.45s ease-in-out'
  },
  deckCardActive: {
    borderColor: theme.palette.primary['clr-300']
  },
  deckCardImage: {
    width: '100%',
    height: '100%',
    display: 'block',
    // The five sheets were not exported to one aspect ratio, so they are fitted
    // rather than stretched. What shows around a narrower one is the card's own
    // white, which is the same white the artwork is drawn on.
    objectFit: 'contain'
  },
  // The blue marker that tags whichever card is currently fronting the deck.
  deckCardMarker: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(2.5),
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary['clr-300'],
    transition: 'opacity 0.45s ease-in-out'
  },
  bannerContainer: {
    position: "relative",
    overflow: "hidden",
    height: "150px",
  },
  commonStyle: {
    display: "flex",
    justifyContent: 'space-between',
    boxShadow: "0px 0px 4px 0px #00000026",
    backgroundColor: theme.palette.shades["clr-white-900"],
    borderRadius: theme.spacing(1),
    position: "absolute",
    width: "100%",
    height: "100%",
    transition: "opacity 0.5s ease-in-out",
  },
  betaContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #E8E4FF 100%)",
    padding: theme.spacing(8, 8, 0, 8),
  },
  comingSoonContainer: {
    background: "linear-gradient(90deg, #FFFFFF 0%, #FFF2DA 75.74%)",
    padding: theme.spacing(8),
  },
  hidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  visible: {
    opacity: 1,
    zIndex: 1,
  },
  bannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
}))

const ExpectImage = 'https://assets.languify.in/images/case-prep-playbook.png';

// Each state is held ~2.5s, with a 450ms ease-in-out transition between them.
const STATE_HOLD_MS = 2500;

// The card at the very back is always the one that has just left the front.
// Animating that move would sweep it right across the whole fan, against the
// run of every other card. It cuts to its new place instead, which goes
// unnoticed because the card taking the front arrives over the top of it.
const cutsToBack = (slot) => slot === DECK_SIZE - 1;

// Where a card sits once it is `slot` places back from the front of the queue.
// Slot 0 is the card belonging to the state currently being shown.
const slotStyle = (slot) => ({
  transform: `translateX(${slot * SLOT_OFFSET}px) scale(${1 - (slot * SLOT_SCALE_STEP)})`,
  opacity: 1 - (slot * SLOT_OPACITY_STEP),
  zIndex: DECK_SIZE - slot,
  ...(cutsToBack(slot) ? { transition: 'none' } : {})
});

// The marker is its own element, so it would otherwise sit at the back of the
// deck fading out on its own while the card carrying it has already cut away.
const markerStyle = (slot) => ({
  opacity: slot === 0 ? 1 : 0,
  ...(cutsToBack(slot) ? { transition: 'none' } : {})
});

const arrowIcon = (
  <img
    src='https://assets.languify.in/images/arrow-right-white.png'
    alt='icon'
    width={20}
  />
);

export const HeroCards = memo(({ setActiveScreen }) => {
  const classes = useStyles();

  // One timer drives both panels, so the two rotations stay in step. The lists
  // are different lengths, hence the separate modulo on each.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), STATE_HOLD_MS);

    return () => clearInterval(interval);
  }, []);

  const handleClick = useCallback(() => {
    setActiveScreen(CaseMasterScreens.CHALLENGES);
  }, [setActiveScreen]);

  const role = CaseHeroRoles[tick % CaseHeroRoles.length];
  const stateIndex = tick % DECK_SIZE;
  const state = CaseHeroStates[stateIndex];

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.panels}>
        <Box className={`${classes.panel} ${classes.expectPanel}`}>
          <Box className={classes.panelContent}>
            <Typography variant='body01-bold' color='danger.clr-700'>
              You&apos;re in a doubt?
            </Typography>
            {/* Broken by hand rather than left to wrap: "ask for roles like"
                has to land on its own line so the role that follows reads as
                the end of the sentence, whatever the panel is doing for
                width. */}
            <Typography variant='h5-medium' color='neutral.clr-600'>
              Not sure what questions an interviewer might
              <br />
              ask for roles like
            </Typography>
            <Typography
              key={role}
              className={classes.fadeState}
              variant='h4-medium'
              color='neutral.clr-900'
            >
              {role}
            </Typography>
          </Box>

          <img src={ExpectImage} alt='case preparation' className={classes.panelImage} />
        </Box>

        <Box className={`${classes.panel} ${classes.coveredPanel}`}>
          <Box className={classes.panelContent}>
            <Typography variant='body01-bold' color='success.clr-700'>
              We&apos;ve got you covered!
            </Typography>
            <Typography variant='h5-medium' color='neutral.clr-400'>
              Practice cases based on
            </Typography>
            <Box key={state.id} className={`${classes.roleBlock} ${classes.fadeState}`}>
              <Typography variant='h4-medium' color='neutral.clr-900'>
                {state.title}
              </Typography>
              <Typography variant='body01-bold'>
                {state.description}
              </Typography>
            </Box>
            <CustomButton
              onClick={handleClick}
              endIcon={arrowIcon}
              sx={{ width: 'fit-content' }}
            >
              Practice Case
            </CustomButton>
          </Box>

          {/* One card per state, reordering itself as the text rotates: the
              state on show takes the front of the queue and the blue marker,
              and everything else steps back one place. */}
          <Box className={classes.deck}>
            {CaseHeroStates.map((deckState, index) => {
              const slot = (index - stateIndex + DECK_SIZE) % DECK_SIZE;

              return (
                <Box
                  key={deckState.id}
                  className={`${classes.deckCard} ${slot === 0 ? classes.deckCardActive : ''}`}
                  style={slotStyle(slot)}
                >
                  <img
                    src={deckState.image}
                    alt={deckState.title}
                    className={classes.deckCardImage}
                  />
                  <Box className={classes.deckCardMarker} style={markerStyle(slot)} />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  )
})

const BetaBanner = memo(() => {
  const classes = useStyles();
  const [showBeta, setShowBeta] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowBeta((prev) => !prev);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box className={classes.bannerContainer}>
      {/* Beta Container */}
      <Box
        className={`${classes.commonStyle} ${classes.betaContainer} ${showBeta ? classes.visible : classes.hidden}`}
      >
        <Box className={classes.bannerContent}>
          <img src="https://assets.languify.in/images/betaTag.png" alt="tag" width={53} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Master case study interviews with business scenarios, problem-solving frameworks, and structured thinking approaches used in consulting and strategic roles.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/codingFlow.png" alt="flow" width={410} />
      </Box>

      {/* Coming Soon Container */}
      <Box
        className={`${classes.commonStyle} ${classes.comingSoonContainer} ${showBeta ? classes.hidden : classes.visible}`}
      >
        <Box className={classes.bannerContent}>
          <img src="https://assets.languify.in/images/comingSoonTag.png" alt="tag" width={104} />
          <Typography variant="h6-medium" color="neutral.clr-800">
            Practice with real business cases from top consulting firms - McKinsey, BCG, Bain and more with detailed frameworks.
          </Typography>
        </Box>
        <img src="https://assets.languify.in/images/30Days.png" alt="flow" width={165} />
      </Box>
    </Box>
  );
})

const HeroSection = ({ setActiveScreen }) => {
  const classes = useStyles();
  return (
    <Box className={classes.container}>
      <HeroCards setActiveScreen={setActiveScreen} />
      {/* <BetaBanner /> */}
    </Box>
  )
}

export default HeroSection
